import { useState, useRef } from "react";
import axios from "axios";
import { LifeBuoy, X, Bug, Lightbulb, Send, Loader2, ImagePlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "bug", label: "Bug", icon: Bug },
  { key: "sugerencia", label: "Sugerencia", icon: Lightbulb },
];

// Downscale + compress to a small JPEG data URL so the payload stays light.
function compressImage(file, maxWidth = 1600, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export function HelpWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("bug");
  const [message, setMessage] = useState("");
  const [image, setImage] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setMessage("");
    setCategory("bug");
    setImage(null);
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("El archivo debe ser una imagen.");
      return;
    }
    try {
      const dataUrl = await compressImage(file);
      setImage(dataUrl);
    } catch {
      toast.error("No se pudo procesar la imagen.");
    }
  };

  const submit = async () => {
    const text = message.trim();
    if (text.length < 3) {
      toast.error("Escribe tu comentario antes de enviar.");
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/feedback`,
        { category, message: text, image: category === "bug" ? image : null },
        { headers }
      );
      toast.success("¡Gracias! Hemos recibido tu comentario.");
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3">
      {open && (
        <div
          data-testid="help-panel"
          className="w-[calc(100vw-3rem)] sm:w-80 bg-background border border-foreground shadow-2xl animate-in slide-in-from-bottom-4 fade-in duration-200"
        >
          <div className="flex items-center justify-between bg-foreground text-background px-4 py-3">
            <p className="font-heading font-bold text-sm">¿Nos ayudas a mejorar?</p>
            <button
              data-testid="help-close-button"
              onClick={() => setOpen(false)}
              className="text-background/70 hover:text-background transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 flex flex-col gap-4">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Categoría</p>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    data-testid={`help-category-${key}`}
                    onClick={() => setCategory(key)}
                    className={`flex items-center justify-center gap-2 h-10 text-xs font-semibold border transition-colors ${
                      category === key
                        ? "bg-primary text-white border-primary"
                        : "bg-transparent text-foreground border-border hover:border-foreground"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Tu comentario</p>
              <Textarea
                data-testid="help-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={2000}
                placeholder={
                  category === "bug"
                    ? "Cuéntanos qué ha fallado y dónde…"
                    : "Cuéntanos tu idea para mejorar…"
                }
                className="rounded-none resize-none h-24 text-sm"
              />
            </div>

            {category === "bug" && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Captura (opcional)
                </p>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onPickImage}
                  className="hidden"
                  data-testid="help-image-input"
                />
                {image ? (
                  <div className="relative border border-border">
                    <img src={image} alt="Captura adjunta" className="w-full max-h-32 object-cover" />
                    <button
                      data-testid="help-image-remove"
                      onClick={() => setImage(null)}
                      className="absolute top-1 right-1 bg-foreground/80 text-background p-1.5 hover:bg-foreground transition-colors"
                      aria-label="Quitar imagen"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid="help-image-pick"
                    onClick={() => fileRef.current?.click()}
                    className="w-full h-10 flex items-center justify-center gap-2 text-xs font-semibold border border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                  >
                    <ImagePlus className="w-4 h-4" />
                    Adjuntar captura
                  </button>
                )}
              </div>
            )}

            <Button
              data-testid="help-submit-button"
              onClick={submit}
              disabled={sending}
              className="rounded-none w-full h-10 bg-primary hover:bg-blue-900 text-white border-0 gap-2"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Enviando…" : "Enviar comentario"}
            </Button>
          </div>
        </div>
      )}

      <button
        data-testid="help-toggle-button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Ayuda y comentarios"
        className="w-14 h-14 rounded-full bg-primary text-white shadow-2xl flex items-center justify-center hover:bg-blue-900 hover:scale-105 transition-all duration-200 self-end"
      >
        {open ? <X className="w-6 h-6" /> : <LifeBuoy className="w-6 h-6" />}
      </button>
    </div>
  );
}
